'use client';

import { Popover } from '@base-ui/react/popover';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { type DriverType } from '@repo/supergraph/marketplace/types';
import { memo, useMemo } from 'react';
import { MdHelpOutline } from 'react-icons/md';
import { DisclosureTooltip } from '../../DisclosureTooltip';
import { summaryTableDetailsCellTooltipClassName } from '../../disclosureTooltipPopover';

interface DetailsCellProps {
  drivers: DriverType[];
  className?: string;
}

export const DetailsCell = memo(({ drivers, className }: DetailsCellProps) => (
  <td
    className={cx(
      css({
        verticalAlign: 'top',
        borderTopColor: 'neutral.lighter2',
        borderRightColor: 'neutral.lighter2',
        borderBottomColor: 'neutral.lighter2',
        borderLeftColor: 'neutral.lighter2',
        paddingTop: {
          base: 'var(--mobile-cell-padding-top)',
          md: 'var(--cell-padding-top)',
        },
      }),
      className,
    )}
  >
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        columnGap: '4',
        rowGap: '4',
        height: 'full',
        paddingTop: '4',
      })}
    >
      {drivers.map(driver => (
        <DriverDetail key={driver.id} driver={driver} />
      ))}
    </div>
  </td>
));

DetailsCell.displayName = 'DetailsCell';

function DriverDetail({ driver }: { driver: DriverType }) {
  const handler = useMemo(() => Popover.createHandle<unknown>(), []);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        columnGap: '1',
        rowGap: '1',
      })}
    >
      <span
        className={css({
          textStyle: 'bodySmallBold',
        })}
      >
        {driver.label}
      </span>
      <span
        className={css({
          textStyle: 'textSmall',
        })}
      >
        {driver.value ?? 'N/A'}
        {driver.valueTooltip && (
          <span
            className={css({
              display: 'inline',
              whiteSpace: 'nowrap',
            })}
          >
            &nbsp;
            <Popover.Trigger
              handle={handler}
              className={css({
                borderTopStyle: 'none',
                borderRightStyle: 'none',
                borderBottomStyle: 'none',
                borderLeftStyle: 'none',
                backgroundColor: 'transparent',
                paddingTop: '0',
                paddingRight: '0',
                paddingBottom: '0',
                paddingLeft: '0',
                cursor: 'pointer',
                display: 'inline-flex',
                color: 'neutral.dark',
                verticalAlign: 'text-bottom',
              })}
              aria-label={`${driver.label} info tooltip`}
            >
              <MdHelpOutline style={{ fontSize: '14px' }} />
            </Popover.Trigger>
            <DisclosureTooltip
              handler={handler}
              tooltipClassName={summaryTableDetailsCellTooltipClassName}
            >
              <span
                className={css({
                  lineHeight: 'xl',
                  marginTop: '0',
                  marginRight: '0',
                  marginBottom: '0',
                  marginLeft: '0',
                  textAlign: 'left',
                  textStyle: 'textSmall',
                  wordWrap: 'break-word',
                })}
              >
                {driver.valueTooltip}
              </span>
            </DisclosureTooltip>
          </span>
        )}
      </span>

      {driver.description && (
        <span
          className={css({
            textStyle: 'textExtraSmall',
            color: 'neutral.darker',
            fontSize: '[10px]',
            lineHeight: '[1.43]',
          })}
        >
          {driver.description}
        </span>
      )}
    </div>
  );
}
